import { useId } from "react";
import {
  ClipboardDocumentCheckIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import SendClockIcon from "./SendClockIcon";

// The mark shown in a suggestion card's hollow tile: the icon for what the suggestion
// does, painted with the brand gradient (pink → purple → blue → orange) so it keeps the
// AI look without the generic sparkle. The same glyphs identify these activities once
// created (see ActivityFeed), so the two stay in parity.

// Fluent glyphs are fill-based (gradient applied as fill); Heroicons are outline
// (gradient applied as stroke).
const FILL_ICONS = {
  PromiseToPay: SendClockIcon,
  PaymentPlan: SendClockIcon,
};
const STROKE_ICONS = {
  PaymentRequestRequested: PaperAirplaneIcon,
  SendEmailRequested: EnvelopeIcon,
  SendDocumentsRequested: EnvelopeIcon,
  CustomerInteractionNoted: PencilSquareIcon,
  CancellationRequested: XCircleIcon,
  TaskRequested: ClipboardDocumentCheckIcon,
};

export default function SuggestionIcon({ type, className = "size-5" }) {
  // useId keeps the gradient unique per instance so cards don't collide.
  const id = useId().replace(/:/g, "");
  // Promises to pay, scheduled payment requests and payment plans all read as the send-clock glyph
  // (they share the send shape); their palettes tell them apart elsewhere. Here every card is painted
  // with the same brand gradient regardless, so the type just selects the glyph.
  const FillIcon = FILL_ICONS[type];
  const Icon = FillIcon ?? STROKE_ICONS[type] ?? PaperAirplaneIcon;
  const paint = FillIcon
    ? { fill: `url(#${id})` }
    : { strokeWidth: 1.7, style: { stroke: `url(#${id})` } };

  return (
    <>
      <svg width="0" height="0" aria-hidden="true" className="absolute">
        <defs>
          <linearGradient id={id} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="35%" stopColor="#a855f7" />
            <stop offset="70%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#fb923c" />
          </linearGradient>
        </defs>
      </svg>
      <Icon aria-hidden="true" className={className} {...paint} />
    </>
  );
}
